import {
  AddressCard,
  AddressCardVerificationProps,
  Divider,
  Menu,
  MenuItem,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { AccountIcon } from '@atgfr8/icons/Account';
import { MenuDownIcon } from '@atgfr8/icons/MenuDown';
import { OwnershipSource } from '@common/index';
import { Contact } from '@store/carriers/carrierDetails/types';
import { CarrierContactT } from '@store/orders/types';
import { CarrierContactVerificationStatuses } from '@views/carriers/carrierDetails/constants';
import { Show, createSignal, type JSX } from 'solid-js';

import classes from './classes';

export type ContactDropdownProps<T> = {
  label?: string;
  contacts: T[];
  onChange: (newContactId: number) => void;
  contactId: number | undefined;
  disabled?: boolean;
  respectVerification?: boolean;
  small?: boolean;
  variant?: 'standard' | 'outlined';
};

type VerificationContact = {
  ownershipSource?: number | null;
  verificationStatus: number;
  phoneVerified?: boolean;
  emailVerified?: boolean;
};

const ITEM_HEIGHT = 40;
const PAPER_STYLES = {
  'overflow-x': 'hidden',
  'overflow-y': 'auto',
  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
  'margin-top': '12px',
  'min-width': '500px',
  'max-height': `${ITEM_HEIGHT * 10}px`,
} satisfies JSX.CSSProperties;

export const ContactDropdown = <T extends CarrierContactT>(
  props: ContactDropdownProps<T>,
) => {
  const [anchorEl, setAnchorEl] = createSignal<undefined | HTMLElement>(
    undefined,
  );
  const [inputElement, setInputElement] = createSignal<
    HTMLInputElement | undefined
  >(undefined);
  const [menuWidth, setMenuWidth] = createSignal<number>(0);
  const [searchValue, setSearchValue] = createSignal('');
  const [focused, setFocused] = createSignal(false);

  const open = () => Boolean(anchorEl());
  const close = () => setAnchorEl(undefined);

  function calculateWidth() {
    const textFieldElement = inputElement();
    if (textFieldElement) setMenuWidth(textFieldElement.clientWidth);
  }

  const findContactById = (contactId: number | undefined) =>
    (Boolean(props.contacts) &&
      props.contacts.find((c) => c.id === contactId)) ||
    ({} as Contact);

  const currentContact = () => findContactById(props.contactId);

  const handleContactClick = (contactId: number | undefined) => {
    const contact = findContactById(contactId);
    if (Boolean(contact)) {
      props.onChange(contact.id);
    }
    close();
  };

  const filteredContacts = () =>
    props.contacts.length > 0
      ? props.contacts.filter((c) => c.id !== props.contactId)
      : [];

  const getVerificationInfo = (
    contact: VerificationContact,
  ): AddressCardVerificationProps | undefined => {
    if (contact.ownershipSource == OwnershipSource.Vendor) {
      return undefined;
    }

    return {
      verificationStatus:
        contact.verificationStatus ===
        CarrierContactVerificationStatuses.UserVerified
          ? CarrierContactVerificationStatuses.Unverified
          : contact.verificationStatus,
      phoneVerified: Boolean(contact.phoneVerified),
      emailVerified: Boolean(contact.emailVerified),
    };
  };

  const shouldDisable = (contact: CarrierContactT) => {
    if (
      !Object.hasOwn(contact, 'verificationStatus') &&
      !Object.hasOwn(contact, 'status')
    ) {
      return false;
    }
    return contact.status === 'Inactive';
  };

  return (
    <>
      <TextField
        label={props.label ?? 'Contact'}
        dataTestId={'contact-dropdown-textInput'}
        ref={setInputElement}
        on:focus={(event: FocusEvent & { currentTarget: HTMLElement }) => {
          setAnchorEl(event.currentTarget);
          calculateWidth();
        }}
        onClick={() => {
          setAnchorEl(inputElement() ?? undefined);
          calculateWidth();
        }}
        onChange={(val) => {
          setSearchValue(val);
        }}
        on:focusin={() => setFocused(true)}
        on:blur={() => {
          setFocused(false);
          setSearchValue('');
        }}
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        value={focused() ? searchValue() : (currentContact().name ?? '')}
        disabled={props.disabled}
        size={Boolean(props.small) ? 'sm' : undefined}
        class="w-full border-x-0 border-t-0 rounded-none! [&.input]:border-b-2!
          [&.input]:border-[#176ec4]! outline-none [&>.floating-label]:left-[0px]!
          [&>.floating-label]:text-lg! [&:focus-within]:border-[#176ec4]!
          [&>.floating-label]:bg-transparent! [&>.floating-label]:top-[-15px]!
          [&:not(.has-value):not(:focus-within)>.floating-label]:top-[-20px]!
          [&:not(.has-value):not(:focus-within)>.floating-label]:font-normal!
          [&:not(.has-value):not(:focus-within)>.floating-label]:text-[#666]!
          [&:not(.has-value):not(:focus-within)>.floating-label]:origin-top-left!
          [&:not(.has-value):not(:focus-within)>.floating-label]:scale-75!
          [&_.floating-label>.strip]:hidden!
          [&.has-value>.floating-label]:text-[#123b50]!
          [&:focus-within>.floating-label]:text-[#123b50]!
          p-[1px]! h-[26px]!"
        startIcon={<AccountIcon size={18} fill="#016fa1" />}
        endIcon={
          <MenuDownIcon
            size={18}
            style={{ transform: open() ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        }
      />
      <Menu
        anchorEl={anchorEl}
        class="[&>ul]:px-1 [&>ul]:m-0"
        style={{
          ...PAPER_STYLES,
          width: menuWidth() + 24 + 'px',
        }}
      >
        <Show
          when={
            typeof props.contactId === 'number' &&
            (!searchValue() ||
              !!currentContact()
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                .name?.toLowerCase()
                .includes(searchValue().toLowerCase()))
          }
        >
          <span class={classes.contactTitleText}>Current Contact</span>
          <MenuItem
            class={classes.menuItem}
            onClick={() => handleContactClick(props.contactId)}
          >
            <div class="w-full!">
              <AddressCard
                contact={currentContact()}
                verificationInfo={getVerificationInfo(currentContact())}
                mode="warning"
                personIconSize={'40px'}
                showIcon
                contactType={currentContact().type as string}
                isCarrierProfile={true}
                ownershipSource={currentContact().ownershipSource ?? undefined}
                contactStatus={currentContact().status ?? undefined}
              />
            </div>
          </MenuItem>
        </Show>
        <Show when={filteredContacts().length > 0}>
          <span class={classes.contactTitleText}>Assign New Contact</span>
          {filteredContacts().map((contact) => {
            if (
              searchValue() !== '' &&
              !contact.name.toLowerCase().includes(searchValue().toLowerCase())
            ) {
              return null;
            }

            return (
              <Show
                when={
                  (props.respectVerification &&
                    contact.verificationStatus ===
                      CarrierContactVerificationStatuses.VendorVerified) ||
                  !props.respectVerification
                }
              >
                <MenuItem
                  class={classes.menuItem}
                  onClick={() => handleContactClick(contact.id)}
                  disabled={shouldDisable(contact)}
                >
                  <div class="w-full!">
                    <AddressCard
                      isCustomerContact={true}
                      verificationInfo={getVerificationInfo(contact)}
                      contactType={contact.type as string}
                      contact={contact}
                      mode="regular"
                      showIcon
                      isCarrierProfile={true}
                      ownershipSource={contact.ownershipSource}
                      contactStatus={contact.status ?? undefined}
                    />
                  </div>
                </MenuItem>
                <Divider class={classes.divider} />
              </Show>
            );
          })}
        </Show>
      </Menu>
    </>
  );
};
